import { useCallback, useEffect, useMemo, useState } from 'react';
import MarkdownIt from 'markdown-it';
import { useApprovals } from '../hooks/useApprovals';
import type { ApprovalContext, ApprovalSample } from '../types/approvals';
import type { TaskStatus } from '../types/monitoring';
import { formatCompactNumber } from '../utils/formatters';
import { getSafeHtmlPreviewDocument } from '../utils/htmlSanitization';
import { formatDuration } from '../utils/timeline';
import { getSampleViewUrl } from '../utils/url';
import { ApprovalCode, highlightApprovalCode } from './ApprovalCode';
import { Pagination } from './Pagination';

const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  highlight: highlightApprovalCode,
});
const PAGE_SIZE = 50;

const OPTION_STYLES: Record<string, string> = {
  approve: 'border-ok/50 bg-ok-tint text-ok hover:bg-ok-tint hover:border-ok',
  reject:
    'border-warn/50 bg-warn-tint text-warn hover:bg-warn-tint hover:border-warn',
  terminate:
    'border-err/50 bg-err-tint text-err hover:bg-err-tint hover:border-err',
};

function ContextText({ text }: { text: string }) {
  const html = useMemo(
    () => getSafeHtmlPreviewDocument(markdown.render(text)),
    [text]
  );
  return (
    <div
      className="approval-code markdown-content space-y-2 break-words text-sm leading-6 [overflow-wrap:anywhere] [&_a]:underline [&_pre]:whitespace-pre-wrap [&_pre]:rounded [&_pre]:bg-surface-2 [&_pre]:p-3 [&_code]:text-xs [&_table]:block [&_table]:overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function RequestContent({ content }: { content: unknown }) {
  const items: unknown[] = Array.isArray(content) ? content : [content];
  return items.map((item, index) => {
    const value = item as {
      type?: string;
      content?: { type?: string; text?: string };
    } | null;
    const block =
      value?.type === 'content'
        ? value.content
        : (value as { type?: string; text?: string } | null);
    return block?.type === 'text' && typeof block.text === 'string' ? (
      <ContextText key={index} text={block.text} />
    ) : (
      <pre
        key={index}
        className="whitespace-pre-wrap break-all font-mono text-xs leading-5"
      >
        {JSON.stringify(item, null, 2)}
      </pre>
    );
  });
}

const CONTEXT_LABELS: Record<string, string> = {
  agent_message_chunk: 'Assistant',
  user_message_chunk: 'User',
  agent_thought_chunk: 'Assistant',
  tool_call: 'Tool call',
  tool_call_update: 'Tool result',
};

// ACP TUI role hues, with darker text for legibility on a light background.
const ROLE_STYLES: Record<string, string> = {
  Assistant: 'text-blue-700 dark:text-[#7aa2f7]',
  User: 'text-amber-800 dark:text-[#e0af68]',
  Operator: 'text-purple-700 dark:text-[#bb9af7]',
  System: 'text-yellow-800 dark:text-[#e6dc7a]',
};

function ContextEntry({ entry }: { entry: ApprovalContext }) {
  const label =
    entry.metadata['inspect.message_role'] === 'system'
      ? 'System'
      : (CONTEXT_LABELS[entry.kind] ?? entry.kind.replaceAll('_', ' '));
  const source = entry.metadata['inspect.user_source'];
  const model = entry.metadata['inspect.model'];
  const fallback = entry.metadata['inspect.model_fallback'];
  const reasoning = entry.kind === 'agent_thought_chunk';
  const heading = (
    <span className="break-words text-sm [overflow-wrap:anywhere]">
      <span
        className={`font-semibold ${ROLE_STYLES[label === 'User' && source === 'operator' ? 'Operator' : label] ?? 'text-muted'}`}
      >
        <span aria-hidden="true">• </span>
        {label}
      </span>
      <span className="text-muted">
        {label === 'Assistant' && model && ` · ${model}`}
        {label === 'Assistant' && fallback && ` (fallback → ${fallback})`}
        {label === 'User' && source && ` · ${source}`}
        {entry.title && ` · ${entry.title}`}
        {reasoning && ' · reasoning'}
      </span>
    </span>
  );
  let toolContent: unknown;
  if (entry.kind === 'tool_call' || entry.kind === 'tool_call_update') {
    try {
      toolContent = (JSON.parse(entry.text) as { content?: unknown }).content;
    } catch {
      // Bounded context may contain truncated JSON; keep the retained text visible.
    }
  }
  const raw = (
    <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-5">
      {entry.text}
    </pre>
  );
  const body = entry.markdown ? (
    <ContextText text={entry.text} />
  ) : Array.isArray(toolContent) && toolContent.length > 0 ? (
    <div className="space-y-3">
      <RequestContent content={toolContent} />
      <details>
        <summary className="min-h-11 cursor-pointer py-3 text-xs text-muted">
          Raw event data
        </summary>
        {raw}
      </details>
    </div>
  ) : (
    raw
  );
  return entry.markdown && !reasoning ? (
    <div className="min-w-0 space-y-2 py-2">
      {heading}
      <div className="pl-4">{body}</div>
    </div>
  ) : (
    <details className="min-w-0">
      <summary className="cursor-pointer py-3">{heading}</summary>
      <div className={`pl-4 ${reasoning ? 'text-muted' : ''}`}>{body}</div>
    </details>
  );
}

function displayTask(task: string) {
  const separator = task.indexOf('/');
  return separator < 0
    ? task
    : task.slice(separator + 1) || task.slice(0, separator);
}

function transcriptLink(
  jobId: string,
  sample: ApprovalSample | undefined,
  tasks: TaskStatus[]
) {
  if (sample) {
    const matches = tasks.filter(
      task =>
        task.task_name === sample.task &&
        (task.samples ?? []).some(
          item =>
            item.id === String(sample.sampleId) && item.epoch === sample.epoch
        )
    );
    // Task/sample labels can repeat across models and retries.
    if (matches.length === 1)
      return {
        href: getSampleViewUrl({
          evalSetId: jobId,
          filename: matches[0].log_file,
          sampleId: String(sample.sampleId),
          epoch: sample.epoch,
        }),
        label: 'Open full transcript ↗',
      };
  }
  return {
    href: `/eval-set/${encodeURIComponent(jobId)}`,
    label: 'Open run logs ↗',
  };
}

function ApprovalReview({
  jobId,
  tasks,
  active,
  autoSelect,
  onRestart,
}: {
  jobId: string;
  tasks: TaskStatus[];
  active: boolean;
  autoSelect: boolean;
  onRestart: (autoSelect?: boolean) => void;
}) {
  const review = useApprovals(jobId);
  const { phase, samples, selectedId, select, refresh } = review;
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(Date.now);
  const [showClientHint, setShowClientHint] = useState(false);
  const [contextOpenFor, setContextOpenFor] = useState<string | null>(null);
  const scrollContextToEnd = useCallback((contents: HTMLDivElement | null) => {
    contents?.parentElement?.scrollIntoView({ block: 'start' });
    contents?.scrollTo(0, contents.scrollHeight);
  }, []);
  useEffect(() => {
    if (phase !== 'reviewing') return;
    const timer = window.setTimeout(() => setShowClientHint(true), 10000);
    return () => window.clearTimeout(timer);
  }, [phase]);
  useEffect(() => {
    if (!active || !autoSelect || phase !== 'ready') return;
    const pending = samples.filter(
      item => item.sessionId && item.pending === 'approval'
    );
    if (pending.length === 1) select(pending[0].sessionId!);
  }, [active, autoSelect, phase, samples, select]);
  useEffect(() => {
    if (!active || phase !== 'ready' || selectedId !== null) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      refresh();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [active, phase, selectedId, refresh]);
  const visibleSamples = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return samples
      .filter(item =>
        [
          String(item.sampleId),
          displayTask(item.task),
          item.agentName ?? '',
        ].some(value => value.toLowerCase().includes(query))
      )
      .sort(
        (a, b) =>
          Number(a.pending == null) - Number(b.pending == null) ||
          Number(a.sessionId == null) - Number(b.sessionId == null) ||
          Number(a.startedAt == null) - Number(b.startedAt == null) ||
          (a.startedAt ?? 0) - (b.startedAt ?? 0) ||
          a.task.localeCompare(b.task) ||
          String(a.sampleId).localeCompare(String(b.sampleId)) ||
          a.epoch - b.epoch
      );
  }, [samples, filter]);
  const totalPages = Math.max(1, Math.ceil(visibleSamples.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageSamples = visibleSamples.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const sample = review.samples.find(
    item => item.sessionId === review.selectedId
  );
  const permission = review.permission;
  const contextId = permission?.request_id ?? '';
  const escalations = review.escalations.filter(
    item => item.tool_call_id === permission?.params.toolCall.toolCallId
  );
  const preceding = review.context.findLast(
    entry =>
      entry.kind === 'agent_message_chunk' &&
      entry.id < (permission?.contextEndId ?? 0)
  );
  const recordedMessage = escalations.findLast(item => item.message)?.message;
  const agentMessage = recordedMessage ?? preceding?.text;
  const agentContext = recordedMessage
    ? review.context.findLast(
        entry =>
          entry.kind === 'agent_message_chunk' &&
          entry.id < (permission?.contextEndId ?? 0) &&
          entry.text === recordedMessage
      )
    : preceding;
  const agentModel = agentContext?.metadata['inspect.model'];
  const transcript = transcriptLink(jobId, sample, tasks);
  const canDecide = active && review.phase === 'pending';
  const rawInput = permission?.params.toolCall.rawInput;
  const argumentsList: [string, unknown][] =
    rawInput && typeof rawInput === 'object' && !Array.isArray(rawInput)
      ? Object.entries(rawInput)
      : [['arguments', rawInput]];
  const status = (
    <div
      role="status"
      aria-live="polite"
      className={`text-center text-sm ${review.phase === 'pending' ? 'sr-only' : ''} ${review.phase === 'applied' ? 'font-medium text-ok' : ''}`}
    >
      {review.phase === 'connecting' && 'Connecting to the run…'}
      {review.phase === 'reviewing' && (
        <>
          Loading context and waiting for the current request.
          {showClientHint && (
            <p className="mt-2 text-muted">
              Another client may be holding this request. If it is open in a
              terminal, disconnect that client and reconnect here.
            </p>
          )}
        </>
      )}
      {review.phase === 'sending' && 'Sending your decision…'}
      {review.phase === 'sent' &&
        'Decision sent · waiting for the runner to confirm it applied.'}
      {review.phase === 'applied' && (
        <>
          {review.message === 'approve'
            ? 'Approved'
            : review.message === 'reject'
              ? 'Rejected'
              : review.message === 'terminate'
                ? 'Sample terminated'
                : (permission?.params.options.find(
                    option => option.optionId === review.message
                  )?.name ?? 'Decision applied')}
          {' · confirmed by the runner.'}
        </>
      )}
      {review.phase === 'unavailable' && (
        <span className="text-warn">Unavailable · {review.message}</span>
      )}
    </div>
  );
  return (
    <div className="min-w-0 space-y-4 pt-4">
      {!permission && phase !== 'ready' && status}
      {review.selectedId === null && review.phase === 'ready' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              aria-label="Filter samples"
              placeholder="Filter samples, tasks, agents…"
              value={filter}
              onChange={event => {
                setFilter(event.target.value);
                setPage(1);
              }}
              className="min-h-11 min-w-0 flex-1 rounded border border-line bg-surface px-3 text-sm"
            />
            <span className="text-xs text-muted">
              {visibleSamples.length} of {samples.length} samples
            </span>
          </div>
          <table
            aria-label="Running samples"
            className="block w-full text-left text-sm md:table md:table-fixed md:text-xs"
          >
            <thead className="hidden text-muted md:table-header-group">
              <tr>
                {[
                  'sample',
                  'epoch',
                  'task',
                  'agent',
                  'pending',
                  'msgs',
                  'tokens',
                  'running',
                ].map((label, index) => (
                  <th
                    key={label}
                    scope="col"
                    className={`px-2 py-2 font-medium ${index === 2 || index === 3 ? 'w-1/5' : ''} ${index > 4 ? 'text-right' : ''}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {pageSamples.map((item, index) => {
                const actionable =
                  item.sessionId != null && item.pending === 'approval';
                const cells = [
                  ['epoch', item.epoch],
                  ['task', displayTask(item.task)],
                  ['agent', item.agentName || '—'],
                  ['pending', item.pending || '—'],
                  [
                    'msgs',
                    item.totalMessages == null
                      ? '—'
                      : formatCompactNumber(item.totalMessages),
                  ],
                  [
                    'tokens',
                    item.totalTokens == null
                      ? '—'
                      : formatCompactNumber(item.totalTokens),
                  ],
                  [
                    'running',
                    item.startedAt == null
                      ? '—'
                      : formatDuration(
                          Math.max(0, now - item.startedAt * 1000)
                        ),
                  ],
                ];
                return (
                  <tr
                    key={
                      item.sessionId ??
                      `${item.task}:${item.sampleId}:${item.epoch}:${index}`
                    }
                    className={`relative mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line px-4 py-3 even:bg-surface-2 focus-within:bg-warn-tint focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-warn md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:px-2 md:py-1 ${active && actionable ? 'cursor-pointer hover:bg-warn-tint' : ''}`}
                  >
                    <td className="min-w-0 py-1 md:px-2">
                      <button
                        type="button"
                        aria-label={`Review sample ${item.sampleId} epoch ${item.epoch}`}
                        disabled={!active || !actionable}
                        onClick={() => item.sessionId && select(item.sessionId)}
                        className="min-h-11 max-w-full break-words text-left text-base font-semibold [overflow-wrap:anywhere] after:absolute after:inset-0 focus-visible:outline-none disabled:text-muted disabled:after:hidden md:min-h-0 md:text-xs md:font-medium"
                      >
                        <span className="md:hidden">Sample </span>
                        {item.sampleId}
                      </button>
                    </td>
                    {cells.map(([label, value]) => (
                      <td
                        key={label}
                        className={`min-w-0 py-1 [overflow-wrap:anywhere] md:px-2 ${label === 'task' || label === 'agent' ? 'basis-full text-muted' : ''} ${label === 'msgs' || label === 'tokens' || label === 'running' ? 'tabular-nums md:text-right' : ''} ${label === 'pending' && item.pending === 'approval' ? 'text-emerald-700 dark:text-[#2ea043]' : ''} ${label === 'pending' && item.pending === 'question' ? 'text-blue-700 dark:text-[#3a8eff]' : ''}`}
                      >
                        <span className="text-muted md:hidden">{label} </span>
                        {value}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="[&_button]:min-h-11">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              total={visibleSamples.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
          {visibleSamples.length === 0 && samples.length > 0 && (
            <p className="text-sm text-muted">No samples match this filter.</p>
          )}
          {!review.samples.some(
            item => item.sessionId && item.pending === 'approval'
          ) && (
            <p className="text-sm text-muted">
              No pending tool approvals are available. The request may have
              finished, or the sample may be waiting for a question or form
              response that still needs the terminal.
            </p>
          )}
          <button
            type="button"
            className="btn btn-ghost min-h-11"
            disabled={!active}
            onClick={review.refresh}
          >
            Refresh samples
          </button>
        </div>
      )}
      {review.selectedId !== null && (
        <div className="flex flex-wrap items-start justify-between gap-3 text-sm">
          <div className="min-w-0 break-words [overflow-wrap:anywhere]">
            <h3 className="font-semibold">
              {sample?.task ?? 'Selected sample'}
            </h3>
            {sample && (
              <p className="text-muted">
                Sample {sample.sampleId} · epoch {sample.epoch}
              </p>
            )}
          </div>
          <a
            className="link inline-flex min-h-11 items-center"
            href={transcript.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {transcript.label}
          </a>
          <button
            type="button"
            className="btn btn-ghost min-h-11"
            disabled={!active || phase === 'sending' || phase === 'sent'}
            onClick={() => onRestart(false)}
          >
            Back to samples
          </button>
        </div>
      )}
      {permission && (
        <section
          aria-label="Requested action"
          className="min-w-0 space-y-3 rounded border border-line bg-surface-2 p-3 sm:p-4"
        >
          <h3 className="break-words text-sm font-semibold [overflow-wrap:anywhere]">
            Tool approval ·{' '}
            {/* Inspect titles append an argument preview after the tool name. */}
            {permission.params.toolCall.title?.split(' ')[0] ||
              permission.params.toolCall.toolCallId}
          </h3>
          {rawInput == null ? (
            <p className="text-sm text-warn">Tool arguments unavailable</p>
          ) : (
            <div>
              {argumentsList.length === 0 && (
                <p className="text-sm text-muted">No arguments</p>
              )}
              <dl className="space-y-3">
                {argumentsList.map(([name, value]) => (
                  <div key={name}>
                    <dt className="mb-1 break-all font-mono text-xs text-muted">
                      {name}
                    </dt>
                    <dd>
                      <ApprovalCode
                        code={
                          typeof value === 'string'
                            ? value
                            : JSON.stringify(value ?? null, null, 2)
                        }
                        language={
                          typeof value !== 'string'
                            ? 'json'
                            : name === 'code'
                              ? 'python'
                              : name === 'command'
                                ? 'bash'
                                : undefined
                        }
                      />
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {escalations.map(item => (
            <section
              key={item.approver}
              aria-label="Monitor review"
              className="space-y-2 border-t border-line pt-3"
            >
              <h4 className="text-sm font-semibold text-warn">
                Review requested by{' '}
                <span className="capitalize">
                  {item.approver.split('/').at(-1)?.replaceAll('_', ' ')}
                </span>
              </h4>
              <ContextText
                text={item.explanation ?? 'No explanation was provided.'}
              />
              {item.truncated && (
                <p className="text-xs text-warn">
                  Monitor context shortened. Open the conversation context or
                  full transcript for more.
                </p>
              )}
            </section>
          ))}
          {agentMessage && (
            <section
              aria-label="Agent message"
              className="space-y-2 border-t border-line pt-3"
            >
              <h4 className="text-sm font-semibold text-blue-700 dark:text-[#7aa2f7]">
                Agent’s message
                {agentModel && (
                  <span className="font-normal text-muted">
                    {' '}
                    · {agentModel}
                  </span>
                )}
              </h4>
              <ContextText text={agentMessage} />
            </section>
          )}
          {rawInput == null && permission.params.toolCall.content != null && (
            <RequestContent content={permission.params.toolCall.content} />
          )}
          {permission.params.options.some(
            option => option.optionId === 'modify'
          ) && (
            <p className="text-xs text-muted">
              Modifying tool arguments is not supported in this view.
            </p>
          )}
          {permission.params.options.some(
            option => option.optionId === 'escalate'
          ) && (
            <p className="text-xs text-muted">
              To escalate to another approver, close this review and use{' '}
              <code>hawk acp</code> in a terminal.
            </p>
          )}
        </section>
      )}
      {review.selectedId !== null && (
        <section
          aria-label="Conversation context"
          className="min-w-0 space-y-3"
        >
          <details
            key={contextId}
            className="scroll-mt-3"
            onToggle={event => {
              if (event.target === event.currentTarget) {
                setContextOpenFor(event.currentTarget.open ? contextId : null);
              }
            }}
          >
            <summary className="min-h-11 cursor-pointer py-3 text-sm font-semibold">
              Conversation context
            </summary>
            {contextOpenFor === contextId && (
              <div
                ref={scrollContextToEnd}
                className="max-h-[65dvh] space-y-3 overflow-y-auto overscroll-contain"
              >
                {rawInput != null &&
                  permission?.params.toolCall.content != null && (
                    <section
                      aria-label="Original approval prompt"
                      className="space-y-2 border-b border-line pb-3"
                    >
                      <h4 className="text-sm font-semibold">
                        Original approval prompt
                      </h4>
                      <RequestContent
                        content={permission.params.toolCall.content}
                      />
                    </section>
                  )}
                {review.contextTruncated && (
                  <p className="text-xs leading-5 text-warn">
                    Some conversation context has been omitted or shortened.
                    Open the full transcript to review it.
                  </p>
                )}
                {review.context.map(entry => (
                  <ContextEntry key={entry.id} entry={entry} />
                ))}
                {review.context.length === 0 && (
                  <p className="text-sm text-muted">
                    No conversation context has arrived yet.
                  </p>
                )}
              </div>
            )}
          </details>
        </section>
      )}
      {(permission ||
        review.phase === 'reviewing' ||
        review.phase === 'unavailable') && (
        <section
          aria-label="Approval controls"
          className="sticky bottom-0 z-10 -mx-4 space-y-2 border-t border-line bg-surface px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {permission && (
            <div className="flex flex-wrap gap-2">
              {permission.params.options.map(option => (
                <button
                  key={option.optionId}
                  type="button"
                  disabled={
                    !canDecide ||
                    ['modify', 'escalate'].includes(option.optionId)
                  }
                  onClick={() => {
                    if (
                      option.optionId === 'terminate' &&
                      !window.confirm(
                        'Terminate this sample? It will stop running.'
                      )
                    )
                      return;
                    review.decide(permission.request_id, option.optionId);
                  }}
                  className={`btn min-h-11 min-w-24 flex-1 whitespace-normal break-words text-sm sm:flex-none ${OPTION_STYLES[option.optionId] ?? 'btn-ghost border-line bg-surface'}`}
                >
                  {option.optionId === 'terminate'
                    ? 'Terminate sample'
                    : option.name}
                </button>
              ))}
            </div>
          )}
          {permission && status}
          {review.queuedCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span>
                {review.queuedCount} more request
                {review.queuedCount === 1 ? '' : 's'} waiting
              </span>
              {(review.phase === 'applied' ||
                review.phase === 'unavailable') && (
                <button
                  type="button"
                  className="btn btn-primary min-h-11"
                  disabled={!active}
                  onClick={review.nextRequest}
                >
                  Next request
                </button>
              )}
            </div>
          )}
          {active &&
            (review.phase === 'unavailable' ||
              review.phase === 'reviewing' ||
              (review.phase === 'applied' && review.queuedCount === 0)) && (
              <button
                type="button"
                className="btn btn-ghost min-h-11"
                onClick={() => onRestart()}
              >
                {review.phase === 'applied'
                  ? 'Review another sample'
                  : 'Reconnect'}
              </button>
            )}
        </section>
      )}
    </div>
  );
}

export function ApprovalsPanel({
  jobId,
  pendingCount,
  active,
  tasks,
}: {
  jobId: string;
  pendingCount: number;
  active: boolean;
  tasks: TaskStatus[];
}) {
  const [generation, setGeneration] = useState<number | null>(null);
  const [autoSelect, setAutoSelect] = useState(true);
  const title =
    pendingCount > 0 ? 'Waiting for human input' : 'Approval review';
  return (
    <section
      aria-label={title}
      className="min-w-0 border-b border-line px-4 py-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          {pendingCount > 0 && (
            <p className="mt-1 text-xs text-muted">
              {pendingCount} sample{pendingCount === 1 ? '' : 's'} waiting for
              human input
            </p>
          )}
        </div>
        {generation === null ? (
          active && (
            <button
              type="button"
              className="btn btn-primary min-h-11"
              onClick={() => {
                setAutoSelect(true);
                setGeneration(0);
              }}
            >
              {pendingCount > 0 ? 'View waiting samples' : 'Review approvals'}
            </button>
          )
        ) : (
          <button
            type="button"
            className="btn btn-ghost min-h-11"
            onClick={() => setGeneration(null)}
          >
            Close review
          </button>
        )}
      </div>
      {!active && (
        <p className="mt-3 text-sm text-muted">
          This run has finished. Approval controls are closed.
        </p>
      )}
      {/* Keep the review mounted for acknowledgements arriving after completion. */}
      {generation !== null && (
        <ApprovalReview
          key={`${jobId}:${generation}`}
          jobId={jobId}
          tasks={tasks}
          active={active}
          autoSelect={autoSelect}
          onRestart={(autoSelect = true) => {
            setAutoSelect(autoSelect);
            setGeneration(value => (value ?? 0) + 1);
          }}
        />
      )}
    </section>
  );
}
